import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SENSOR_TYPES, SENSOR_TYPE_LABELS } from '../../types/lawnTypes';

/**
 * Inline form anchored to the spot the user clicked on the property image.
 *
 * Only collects what a person can reasonably know at install time: a name and a
 * sensor type. Battery and moisture are seeded by the data layer so a new pin
 * renders like a real one.
 */
export default function AddSensorForm({ position, onSubmit, onCancel, isSaving }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('soil_moisture');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      type,
      xPct: position.xPct,
      yPct: position.yPct,
    });
  };

  // Flip the popover toward the inside of the image when near an edge.
  const flipX = position.xPct > 62;
  const flipY = position.yPct > 68;

  return (
    <form
      className={`lawn-add-sensor${flipX ? ' lawn-add-sensor-flip-x' : ''}${
        flipY ? ' lawn-add-sensor-flip-y' : ''
      }`}
      style={{ left: `${position.xPct}%`, top: `${position.yPct}%` }}
      onSubmit={handleSubmit}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
    >
      <p className="lawn-add-sensor-title">New sensor here</p>

      <label htmlFor="lawn-sensor-name">Name</label>
      <input
        id="lawn-sensor-name"
        ref={inputRef}
        type="text"
        value={name}
        placeholder="e.g. Front yard"
        onChange={(event) => setName(event.target.value)}
        maxLength={40}
      />

      <label htmlFor="lawn-sensor-type">Type</label>
      <select
        id="lawn-sensor-type"
        value={type}
        onChange={(event) => setType(event.target.value)}
      >
        {SENSOR_TYPES.map((option) => (
          <option key={option} value={option}>
            {SENSOR_TYPE_LABELS[option]}
          </option>
        ))}
      </select>

      <p className="lawn-add-sensor-position">
        Pinned at {Math.round(position.xPct)}%, {Math.round(position.yPct)}%
      </p>

      <div className="lawn-add-sensor-actions">
        <button type="button" onClick={onCancel} className="lawn-button-secondary">
          Cancel
        </button>
        <button
          type="submit"
          className="green-button"
          disabled={!name.trim() || isSaving}
        >
          {isSaving ? 'Adding…' : 'Add sensor'}
        </button>
      </div>
    </form>
  );
}

AddSensorForm.propTypes = {
  position: PropTypes.shape({
    xPct: PropTypes.number.isRequired,
    yPct: PropTypes.number.isRequired,
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

AddSensorForm.defaultProps = {
  isSaving: false,
};
